import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon/Icon";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { COLORS, COMMON, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    acceptGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeGridItem?: GridItemProps;
    closeButton?: ButtonPresentationProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        size: 100,
        isAutoSize: true,
        cssOverrides: {
            scrim: css`
                background-color: none !important;
                height: 0px !important;
            `,

            drawerContainer: css`
                div[class^="ContentBody"] {
                    height: auto;
                }

                @media print {
                    display: none !important;
                }
            `,
            drawerBody: css`
                @media print {
                    display: none !important;
                }
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                padding: 15px;
                margin: 0 auto;
                width: 100%;
                > div[class^="GridWrapper"] {
                    max-width: ${COMMON.width.content}px;
                }
            `,
        },

        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        css: css`
            margin: 0 auto;
            align-items: center;
            > div[class^="GridOffset"] {
                justify-content: space-between;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > div[class^="GridOffset"] {
                            justify-content: flex-start;
                        }
                    `,
                    css`
                        > div[class^="GridOffset"] {
                            justify-content: flex-start;
                        }
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    messageGridItem: {
        width: [12, 12, 10, 10, 10],
        css: css`
            span {
                color: ${getColor("text.main")};
            }
            align-self: center;
            justify-content: center;
        `,
    },
    acceptGridItem: {
        width: [8, 9, 2, 2, 2],
        css: css`
            justify-content: flex-start;
        `,
    },
    acceptButton: {
        css: css`
            max-width: 120px;
            border-radius: 0;
            padding: 10px 34px !important;
            background: ${getColor("primary")};
            border: 2px solid ${getColor("primary")};
            span[class^="TypographyStyle"] {
                color: ${getColor("secondary")};
                font-size: 14px;
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
            &:hover {
                background: ${getColor("text.accent")};
                span[class^="TypographyStyle"] {
                    color: ${getColor("secondary")};
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: 46px;
                    `,
                    css`
                        min-height: 46px;
                    `,
                    css`
                        span {
                            font-size: 14px;
                        }
                    `,
                    css`
                        span {
                            font-size: 14px;
                        }
                    `,
                    null,
                ])}
        `,
    },
    closeGridItem: {
        width: [4, 3, 2, 1, 1],
    },
    closeButton: {
        css: css`
            width: 100%;
            line-height: 0;
            border-radius: 0;
            padding: 10px 34px !important;
            background: ${getColor("common.background")};
            border: 2px solid ${getColor("primary")} !important;
            &:hover {
                background: ${getColor("common.background")};
                border: 2px solid ${getColor("text.accent")} !important;
            }
            > span[class^="IconWrapper"] {
                height: 22px !important;
                color: ${getColor("text.main")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        min-height: 46px;
                        padding: 8px 18px !important;
                        span {
                            font-size: 14px;
                        }
                    `,
                    css`
                        padding: 8px 18px !important;
                        min-height: 46px;
                        span {
                            font-size: 14px;
                        }
                    `,
                    css`
                        min-height: 46px;
                    `,
                ])}
        `,
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const location = useAppSelector(o => getLocation(o));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("acceptCookies")) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        setCookie("acceptCookies", "true", { expires: 365 });
    };

    const closeHandler = () => {
        setIsOpen(false);
    };

    return (
        <Drawer isOpen={isOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            <GridContainer {...styles.container}>
                <GridItem {...styles.messageGridItem}>
                    <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                        {siteMessage("Core_CookiePrivacyPolicy")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.acceptGridItem}>
                    <Button {...styles.acceptButton} onClick={acceptHandler} data-test-selector="cookiePolicy_AcceptCookies">
                        {siteMessage("Core_CookiePrivacyPolicyAccept")}
                    </Button>
                </GridItem>
                {/* <GridItem {...styles.closeGridItem}>
                    <Button
                        {...styles.closeButton}
                        onClick={closeHandler}
                        data-test-selector="cookiePolicy_ClosePolicy"
                        role="button"
                        aria-label="Close Policy"
                    >
                        <Icon src={X}></Icon>
                    </Button>
                </GridItem> */}
            </GridContainer>
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
